import { SearchOrigin, toSearchOrigin, BUSINESS_EMAIL_URL_TOKEN, MAILBOX_SITE_LEVEL_REFERRAL } from '@wix/domains-serverless-interface';
import { ViewMode } from '../types';
const REFERRAL_FOCUS_WIDGET = 'dashboard-focus-widget';
const BNG_REFERRER_PATHS = ['/tools/business-name-generator', '/domain/result/bng'];
const WHOIS_REFERRER_PATHS = ['/domains/whois', '/domain/result/whois'];
const getReferrerPathname = () => {
  try {
    return new URL(document.referrer).pathname;
  } catch {
    return '';
  }
};
const getCurrentUrl = () => {
  try {
    return window.location.href;
  } catch {
    return '';
  }
};

// 1052 (Storefront): search_origin keys off the page source (referrer).
const isBusinessEmailReferrer = () => {
  try {
    return document.referrer.includes(BUSINESS_EMAIL_URL_TOKEN);
  } catch {
    return false;
  }
};

// 1053/1054: view_page keys off the current URL.
const isBusinessEmailUrl = () => getCurrentUrl().includes(BUSINESS_EMAIL_URL_TOKEN);

// 1052 (BM): search_origin keys off the my-mailboxes site-level referralInfo.
const isMailboxSiteLevelReferral = () => getCurrentUrl().includes(MAILBOX_SITE_LEVEL_REFERRAL);
const resolveStorefrontOrigin = isOnResultsPage => {
  if (isOnResultsPage) {
    return SearchOrigin.STOREFRONT;
  }
  const referrerPath = getReferrerPathname();
  if (BNG_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
    return SearchOrigin.STOREFRONT_BNG;
  }
  if (WHOIS_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
    return SearchOrigin.STOREFRONT_WHOIS;
  }
  return SearchOrigin.STOREFRONT;
};
export const resolveSearchOrigin = _ref => {
  let {
    isOnResultsPage,
    referralAdditionalInfo,
    viewMode,
    useReferralAdditionalInfoAsSearchOrigin = false
  } = _ref;
  if (viewMode === ViewMode.BASE44) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BASE44 : SearchOrigin.DOMAINS_GATE_BASE44;
  }
  if (viewMode === ViewMode.FUNNEL_GATE || viewMode === ViewMode.FUNNEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_MAIN_FUNNEL_AI_CHAT : SearchOrigin.DOMAINS_GATE_MAIN_FUNNEL_AI_CHAT;
  }
  if (viewMode === ViewMode.WHOIS) {
    return SearchOrigin.STOREFRONT_WHOIS;
  }
  if (viewMode === ViewMode.STOREFRONT_BNG) {
    return SearchOrigin.STOREFRONT_BNG;
  }
  if (viewMode === ViewMode.STOREFRONT) {
    // Storefront Business Email: page source (referrer) contains business-email.
    if (isBusinessEmailReferrer()) {
      return SearchOrigin.STOREFRONT_BUSINESS_EMAIL;
    }
    return resolveStorefrontOrigin(isOnResultsPage);
  }
  // BM Business Email: URL carries the my-mailboxes site-level referralInfo.
  // Gate landing vs. results-page re-search get distinct origins.
  if (isMailboxSiteLevelReferral()) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BUSINESS_EMAIL : SearchOrigin.DOMAINS_GATE_BUSINESS_EMAIL;
  }
  if (viewMode === ViewMode.ACCOUNT_LEVEL_GATE || viewMode === ViewMode.ACCOUNT_LEVEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_ACCOUNT_LEVEL : SearchOrigin.DOMAINS_GATE_ACCOUNT_LEVEL;
  }
  const fallback = isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE : SearchOrigin.DOMAINS_GATE;
  if (useReferralAdditionalInfoAsSearchOrigin && !isOnResultsPage && referralAdditionalInfo) {
    return toSearchOrigin({
      value: referralAdditionalInfo,
      fallback
    });
  }
  if (isOnResultsPage) {
    if (!referralAdditionalInfo) {
      const referrerPath = getReferrerPathname();
      if (BNG_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
        return SearchOrigin.STOREFRONT_BNG;
      }
      if (WHOIS_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
        return SearchOrigin.STOREFRONT_WHOIS;
      }
    }
    return SearchOrigin.DOMAINS_RESULTS_PAGE;
  }
  if (referralAdditionalInfo === REFERRAL_FOCUS_WIDGET) {
    return SearchOrigin.FOCUS_WIDGET;
  }
  return SearchOrigin.DOMAINS_GATE;
};
export const resolveViewPage = function (viewMode, isOnResultsPage) {
  if (isOnResultsPage === void 0) {
    isOnResultsPage = true;
  }
  if (viewMode === ViewMode.BASE44) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BASE44 : SearchOrigin.DOMAINS_GATE_BASE44;
  }
  if (viewMode === ViewMode.WHOIS) {
    return SearchOrigin.STOREFRONT_WHOIS;
  }
  if (viewMode === ViewMode.FUNNEL_GATE || viewMode === ViewMode.FUNNEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_MAIN_FUNNEL : SearchOrigin.DOMAINS_GATE_MAIN_FUNNEL;
  }
  if (viewMode === ViewMode.STOREFRONT || viewMode === ViewMode.STOREFRONT_BNG) {
    // Storefront Business Email: current URL (e.g. /business-email) contains business-email.
    if (viewMode === ViewMode.STOREFRONT && isBusinessEmailUrl()) {
      return SearchOrigin.STOREFRONT_BUSINESS_EMAIL;
    }
    return viewMode;
  }
  if (isMailboxSiteLevelReferral() || isBusinessEmailUrl()) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BUSINESS_EMAIL : SearchOrigin.DOMAINS_GATE_BUSINESS_EMAIL;
  }
  if (viewMode === ViewMode.ACCOUNT_LEVEL_GATE || viewMode === ViewMode.ACCOUNT_LEVEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_ACCOUNT_LEVEL : SearchOrigin.DOMAINS_GATE_ACCOUNT_LEVEL;
  }
  return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE : SearchOrigin.DOMAINS_GATE;
};
// 17:506 "Search Domain > Result Page Error" is published only in the legacy
// @wix/bi-logger-premium-data package, which this repo is migrating away
// from (/migrate-bi-events). This mirrors that generated function verbatim —
// swap to the @wix/bi-logger-premium-data-domains import once the event is
// added there.
export const searchDomainResultPageErrorSrc17Evid506 = params => ({
  evid: 506,
  src: 17,
  endpoint: '',
  params
});
//# sourceMappingURL=useDomainSearchGateBi.utils.js.map